import { useComposedRefs } from "../../utils/use-composed-refs.js";
import { renderElement } from "../../utils/use-render.js";
import { useMenuContext, useMenuPopupContext } from "./context.js";
import { callKeyDownHandler, preventMenuKeyDefault } from "./keyboard.js";
import { MenuContentDataAttrs } from "@videojs/core";
import { isMenuNavigationKey } from "@videojs/core/dom";
import { forwardRef, useCallback, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
//#region src/ui/menu/content.tsx
/** Accessible item and focus scope for exactly one root or nested menu page. */
const MenuContent = forwardRef(function MenuContent({ render, className, style, onKeyDown, onBlur, ...elementProps }, forwardedRef) {
	const { core, menu, parent, state, contentId } = useMenuContext();
	const { popup, element: popupElement } = useMenuPopupContext();
	const isSubmenu = parent !== null;
	const isActive = isSubmenu && state.open;
	const wasActiveRef = useRef(false);
	const cleanupRegistrationRef = useRef(null);
	useLayoutEffect(() => {
		if (!isSubmenu) return void 0;
		const wasActive = wasActiveRef.current;
		wasActiveRef.current = isActive;
		if (isActive && !wasActive) {
			const frame = requestAnimationFrame(() => menu.highlightFirstItem({ preventScroll: true }));
			return () => cancelAnimationFrame(frame);
		}
	}, [
		isActive,
		isSubmenu,
		menu
	]);
	const setContentElement = useCallback((element) => {
		cleanupRegistrationRef.current?.();
		cleanupRegistrationRef.current = null;
		if (!element) {
			if (isSubmenu) menu.setPopupElement(null);
			return;
		}
		if (isSubmenu) menu.setPopupElement(element);
		cleanupRegistrationRef.current = popup.registerContent({
			menu,
			parent: parent?.menu ?? null,
			element
		});
	}, [
		isSubmenu,
		menu,
		parent?.menu,
		popup
	]);
	const composedRef = useComposedRefs(forwardedRef, setContentElement);
	const handleKeyDown = useCallback((event) => {
		const defaultPreventedByUser = callKeyDownHandler(onKeyDown, event);
		const isNavigationKey = isMenuNavigationKey(event);
		menu.contentProps.onKeyDown(event);
		if (isSubmenu && (event.key === "ArrowLeft" || event.key === "Escape") && !defaultPreventedByUser) {
			event.preventDefault();
			menu.close("escape");
		}
		if (event.key !== "Escape" && isNavigationKey) event.stopPropagation();
	}, [
		isSubmenu,
		menu,
		onKeyDown
	]);
	if (isSubmenu && !isActive && state.status !== "ending") return null;
	const contentNode = renderElement("div", {
		render,
		className,
		style
	}, {
		state,
		stateAttrMap: MenuContentDataAttrs,
		ref: composedRef,
		props: [
			{
				id: contentId,
				...core.getContentAttrs()
			},
			{
				onKeyDownCapture: preventMenuKeyDefault,
				onKeyDown: handleKeyDown,
				onBlur: (event) => {
					onBlur?.(event);
					if (isSubmenu) menu.contentProps.onFocusOut(event);
				}
			},
			elementProps
		]
	});
	return isSubmenu && popupElement ? createPortal(contentNode, popupElement) : contentNode;
});
//#endregion
export { MenuContent };

//# sourceMappingURL=content.js.map